import type { ChangeEvent, RefObject } from 'react'

import Icon from '@/shared/components/atoms/Icon'
import { cn } from '@/shared/utils/helpers'

interface DesktopVolumeControlProps {
	isMuted: boolean
	setIsMuted: (isMuted: boolean) => void
	volume: number
	setVolume: (volume: number) => void
	audioElementRef: RefObject<HTMLAudioElement | null>
	className?: string
}

export const DesktopVolumeControl = ({
	isMuted,
	setIsMuted,
	volume,
	setVolume,
	audioElementRef,
	className,
}: DesktopVolumeControlProps) => {
	const handleToggleMute = () => {
		const nextIsMuted = !isMuted
		setIsMuted(nextIsMuted)

		if (audioElementRef.current) {
			audioElementRef.current.volume = nextIsMuted ? 0 : volume
		}
	}

	const handleVolumeChange = (event: ChangeEvent<HTMLInputElement>) => {
		const newVolume = parseFloat(event.target.value)
		setVolume(newVolume)
		setIsMuted(newVolume === 0)

		if (audioElementRef.current) {
			audioElementRef.current.volume = newVolume
		}
	}

	return (
		<div className={cn('flex items-center gap-2 w-[120px]', className)}>
			<button
				className="bg-transparent border-none cursor-pointer p-1 flex items-center justify-center opacity-70 transition-opacity duration-200 hover:opacity-100"
				onClick={handleToggleMute}
			>
				<Icon
					name={isMuted || volume === 0 ? 'volume-mute-fill' : 'volume-down'}
					width="20px"
					height="20px"
					color="var(--foreground)"
				/>
			</button>
			<input
				type="range"
				min="0"
				max="1"
				step="0.01"
				value={isMuted ? 0 : volume}
				onChange={handleVolumeChange}
				className={cn(
					'appearance-none w-[80px] h-1 bg-border rounded-[2px] outline-none cursor-pointer m-0 p-0',
					'[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:bg-foreground [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:mt-[0px] [&::-webkit-slider-thumb]:transition-transform [&::-webkit-slider-thumb]:duration-100 hover:[&::-webkit-slider-thumb]:scale-125',
					'[&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:bg-foreground [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:border-none',
					'[&::-moz-range-progress]:bg-foreground [&::-moz-range-progress]:h-1 [&::-moz-range-progress]:rounded-[2px]'
				)}
			/>
		</div>
	)
}
